// The file field (spec §8): one file, read in the browser into { content (data url), file_name, is_base_64: true, size } like
// the older file-input, and sent inside the submission. Name, type and the 5 MB limit are checked BEFORE reading so a
// refused file is never loaded into memory; the server checks everything again.
import { h } from "../h.js";
import { slot_props } from "../styles.js";
import { label_of, error_message } from "../messages.js";
import { FILE_ACCEPT, DEFAULT_MAX_FILE_BYTES } from "../constants.js";
import { check_file, read_file_value, format_file_size } from "../format.js";
import { FieldShell, control_aria } from "./shell.js";
import { accept_extensions } from "../site_helpers.js";
import { allowed_file_types } from "../form_helper.js";

// the accept text of one file field: the formats the admin chose for it (`field.data.accept`), or every format the form builder takes
export const field_accept = (field) => (Array.isArray(field?.data?.accept) && field.data.accept.length > 0 ? allowed_file_types(field).map((extension) => `.${extension}`).join(",") : FILE_ACCEPT);

// `accept` (optional, added for jument): a site's file input that takes fewer formats than the form builder (a logo: images
// only) passes its own accept text; a file outside it is refused with the formats it does take, before the file is read.
const outside_accept = (file, accept) => {
    const allowed = accept_extensions(accept);
    if (allowed.length === 0) {
        return null;
    }
    const name = String(file?.name ?? "");
    const extension = name.lastIndexOf(".") < 0 ? "" : name.slice(name.lastIndexOf(".") + 1).toLowerCase();
    return allowed.includes(extension) ? null : { code: "file_type_not_allowed", detail: allowed.join(",") };
};

// the change handler, separate so it can be tested: file in, field value or field error out. `field_accept`: the field's own formats
// (field_accept above); `accept`: a site's narrower list on top of them
export const handle_file_chosen = async (file, { on_change, on_error, read = read_file_value, max_bytes = DEFAULT_MAX_FILE_BYTES, accept, field_accept: own_accept }) => {
    if (!file) {
        return;
    }
    const problem = (own_accept ? outside_accept(file, own_accept) : null) || (accept ? outside_accept(file, accept) : null) || check_file(file, max_bytes);
    if (problem) {
        on_error([problem.detail === undefined ? problem.code : `${problem.code}||${problem.detail}`]);
        return;
    }
    try {
        on_change(await read(file));
    } catch {
        on_error(["file_read_failed"]);
    }
};

export const FileField = ({ field, id, value, on_change, on_error, error, required, disabled, styles, class_names, lang, messages, max_file_bytes = DEFAULT_MAX_FILE_BYTES }) => {
    const style_args = { class_names, styles, type: field.type };
    const control = value
        ? h(
            "div",
            { className: class_names?.file_chip, "data-has-file": "true" },
            h("span", { className: class_names?.file_name }, value.file_name || value.name),
            value.size ? h("span", { className: class_names?.file_size }, ` ${format_file_size(value.size)}`) : null,
            h("button", {
                type: "button",
                disabled: disabled ? true : undefined,
                className: class_names?.file_remove,
                "aria-label": `${label_of("remove_file", lang, messages)}: ${value.file_name || value.name || ""}`,
                onClick: () => on_change(undefined),
            }, label_of("remove_file", lang, messages)),
        )
        : h("input", {
            ...control_aria({ id, field, error, required, disabled }),
            ...slot_props({ slot: "input", ...style_args }),
            type: "file",
            accept: field_accept(field),
            onChange: (event) => {
                const file = event.target.files?.[0];
                handle_file_chosen(file, { on_change, on_error: on_error || (() => {}), max_bytes: max_file_bytes, field_accept: field_accept(field) });
            },
        });
    return h(FieldShell, { field, id, error, required, disabled, styles, class_names, variant: "file" }, control);
};

export { error_message };
